import React, { useContext, useEffect, useState } from "react";
import { Box, IconButton, Accordion, AccordionDetails, AccordionSummary, Typography, Divider, Button, Input } from "@mui/joy";
import { ArrowBack, PersonOutlined, Edit } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { axiosInstance } from "../../helpers/AxiosConfiguration"; 
import AutoBuildFormSection from "../../components/AutomaticFormBuilder";
import RenderDifferentIcons from "../../components/AccordionIcons";
import FeedIcon from '@mui/icons-material/Feed';
import { convertJSONKeyToTitle } from "../../helpers/KeyConverters";
import qs from 'qs';
import { AlertContext } from "../../components/AlertContext";
import { putVisit } from "../../services/Visit";
import { dateToISO, isoToDate, isoToTime, timeToISO } from "../../helpers/TimeConversionHelper";
import { giveBackUserViewVisit } from "../../helpers/DecideBetweenUserEditOrGenerated";
import { patientFormFields, visitFormFields } from "./NewVisit";
import NavBlocker from "../../components/NavBlocker";



export default function PastReportsEditingPage() {
    const { visitID } = useParams();
    const { addAlert } = useContext(AlertContext)
    const navigator = useNavigate();
    const [dirtyForm, setDirtyForm] = useState(false);
    const [visit, setVisit] = useState(null);
    const [formTop, setFormTop] = useState({});
    const [allFormField, setAllFormField] = useState(null);
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [localTitle, setLocalTitle] = useState('');

    const fetchEverything = async () => {
        try {
            const fetchedVisit = await axiosInstance.get(`/api/v1/visits/${visitID}`);
            const fetchedFormFields = await axiosInstance.get('/api/v1/organization/details');
            
            const combineVisitResponse = giveBackUserViewVisit(fetchedVisit.data);
            fetchedVisit.data['call_info'] = combineVisitResponse['call_info']
            fetchedVisit.data['treatment_info'] = combineVisitResponse['treatment_info']
            fetchedVisit.data['vitals_info'] = combineVisitResponse['vitals_info']
            fetchedVisit.data['details']['date'] = isoToDate(fetchedVisit.data['details']['incident_occurred_at']);
            if (fetchedVisit.data['patient_info']['date_of_birth']) {
                fetchedVisit.data['patient_info']['date_of_birth'] = isoToDate(fetchedVisit.data['patient_info']['date_of_birth']);
            }
            fetchedVisit.data['details']['efrt_activation_time'] = isoToTime(fetchedVisit.data['details']['efrt_activated_at']);


            setVisit(fetchedVisit.data);
            setFormTop({
                "date": fetchedVisit.data['details']['date'],
                "efrt_activation_time": fetchedVisit.data['details']['efrt_activation_time'],
                "ambulance_number": fetchedVisit.data.details["ambulance_number"],
                "transport": fetchedVisit.data.details["transport"]
            })
            setAllFormField(fetchedFormFields.data);
        } catch(error) {
            console.log(error)
        }
    }

    
    useEffect(() => {
        fetchEverything()
    }, [])

    const saveNewForm = async(event) => {
        try {
            event.preventDefault()
            const formData = new FormData(event.target);
            const values = Object.fromEntries(formData.entries());
            const nested = qs.parse(values);
            if ('date_of_birth' in nested.patient_info && typeof nested.patient_info['date_of_birth'] === "string" && nested.patient_info['date_of_birth'].length > 0) {
                nested.patient_info['date_of_birth'] = dateToISO(nested.patient_info['date_of_birth']);
            } else {
                delete nested.patient_info['date_of_birth']
            }
            nested.details['date'] = dateToISO(nested.details['date']);
            nested.details['efrt_activation_time'] = timeToISO(nested.details['efrt_activation_time']);
    
            try {
                const response = await putVisit({ 
                    visitId: visitID, 
                    title: localTitle,
                    patientInfo: nested.patient_info,
                    details: nested.details,
                    callInfo: nested.call_info,
                    treatmentInfo: nested.treatment_info,
                    vitalsInfo: nested.vitals_info,
                });
    
                addAlert("Saved!", "success");
            } catch(error) {
                addAlert(error.message, "danger");
            }
        } catch (error) {
            addAlert("Unable to save report.", "danger")
            console.log(error)
        }
    }

    const handleTitleChange = (event) => {
        setVisit(prev => ({ ...prev, title: event.target.value }));
        setLocalTitle(event.target.value)
        setDirtyForm(true);
    }

    const toggleTitleEdit = () => {
        setIsEditingTitle(!isEditingTitle);
    }

    const handleDirty = (_) => {
        setDirtyForm(true);
    }

    const handleInvalid = (event) => {
        event.preventDefault();
        addAlert(`Save failed. ${event.target.name} is invalid`, "danger")
    }

    return <Box sx={{height: '100vh', padding: 2.5}}>
        <NavBlocker />
        <form 
        onSubmit={saveNewForm}
        onInput={handleDirty}
        onInvalid={handleInvalid}
        >
        <Box>
            <IconButton onClick={() => {
                navigator(-1)
                }} sx={{ mr: 2 }}>
                <ArrowBack />
            </IconButton>
        </Box>
        {visit && allFormField && <Box sx={{height: "80vh", marginTop: '2vh', marginBottom: '1vh', overflowY: "auto"}}>
                <Box>
                    {/* Date, EMS Activation, Ambulance #, Transport*/}
                    {isEditingTitle ? (
                        <Input
                            value={(visit.title !== null && visit.title !== '' && visit.title !== 'null') ? visit.title : ''}
                            onChange={handleTitleChange}
                            onBlur={toggleTitleEdit}
                            endDecorator={<Edit />}
                        />
                    ) : (
                        <Typography 
                            level='h2' 
                            onClick={toggleTitleEdit}
                            sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                        >
                            {(visit.title !== null && visit.title !== '' && visit.title !== 'null') ? visit.title : `Report written on ${new Date(visit.details.incident_occurred_at).toLocaleDateString()}`}
                            <IconButton onClick={toggleTitleEdit} size="small">
                                <Edit />
                            </IconButton>
                        </Typography>
                    )}
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{p: 2, border: "1px #c5cfd3 solid", borderRadius: "10px", overflow: 'auto'}} key={'visit_form'}>
                            <Typography level='h3' startDecorator={<FeedIcon fontSize="xl4"/>}> Report Information</Typography>
                            <Divider sx={{my: 1}}/>
                            <AutoBuildFormSection formFields={visitFormFields} initialInput={formTop} section="details"/>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{p: 2, border: "1px #c5cfd3 solid", borderRadius: "10px", overflow: 'auto'}} key={'patient_form'}>
                        <Typography level='h3' startDecorator={<PersonOutlined fontSize="xl4"/>}> Patient Information</Typography>
                        <Divider sx={{my: 1}}/>
                        <AutoBuildFormSection formFields={patientFormFields} initialInput={visit['patient_info']} section='patient_info'/>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                </Box>
                <Box>
                    <Box>
                        {Object.keys(allFormField['form_fields']).map( key => {
                            return <Box>
                                    <Accordion square={false} variant="outlined" sx={{borderBottom: "1px #c5cfd3 solid", borderRadius: "10px"}} key={key}>
                                    <AccordionSummary sx={{
                                        '.MuiAccordionSummary-button': {
                                            borderRadius: "10px"
                                        }}} >
                                        {/* Need logic to render an icon based off of the title of the section*/}
                                        <Box sx={{display: "flex", pl: 1.5, gap: 1, alignItems: "center"}}>
                                            <RenderDifferentIcons label={key}/>
                                            <h3>{convertJSONKeyToTitle(key)}</h3>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{overFlowX: "scroll",
                                        '.MuiAccordionDetails-content': {
                                            overflowX: 'auto'
                                        }
                                    }}>
                                        <Divider sx={{mb: 1}}/>
                                        <AutoBuildFormSection formFields={allFormField['form_fields'][key]} initialInput={visit[key]} section={key}/>
                                    </AccordionDetails>
                                </Accordion>
                                <Divider sx={{ my: 2 }} />
                            </Box>
                        })}
                    </Box>
                </Box>
            </Box>
        }
        <Box sx={{display: "flex", justifyContent: "end", gap: 1}}>
            <Button type="submit" sx={{width: "100%"}}>Save</Button>
        </Box>
        </form>
    </Box>
}